/**
 * services/liveChat.js
 * Живой чат через Socket.IO:
 * 1) старт сессии через HTTP
 * 2) join в комнату по session_id
 * 3) обмен сообщениями в realtime
 */
import { io } from 'socket.io-client';
import { apiService } from './api';

const CHAT_START_ENDPOINT = import.meta.env.VITE_CHAT_START_ENDPOINT || '/chat/start';
const CHAT_RESUME_ENDPOINT = import.meta.env.VITE_CHAT_RESUME_ENDPOINT || '/chat/resume';
const CHAT_HISTORY_ENDPOINT_TEMPLATE =
  import.meta.env.VITE_CHAT_HISTORY_ENDPOINT || '/chat/history/by-session/{session_id}';
const CHAT_USER_ENDPOINT_TEMPLATE =
  import.meta.env.VITE_CHAT_USER_ENDPOINT || '/chat/user/{session_id}';
const SOCKET_URL = import.meta.env.VITE_WS_URL || window.location.origin;
const SOCKET_PATH = import.meta.env.VITE_SOCKET_PATH || '/socket.io';
const SEND_TIMEOUT_MS = 10000;

class LiveChatService {
  constructor() {
    this._sessionId = null;
    this._socket = null;
    this._listeners = [];
    this._needsResumeListeners = [];
    this._connectionListeners = [];
    this._needsResume = false;
    this._startPromise = null;
    this._connectPromise = null;
    this._pendingSends = [];
  }

  /**
   * Начать сессию с менеджером.
   * Если сессия уже закрыта на бэке — не подключаем сокет, ждём resume.
   * @param {Object} userData - данные пользователя
   * @returns {Promise<{ sessionId: string, closed?: boolean }>}
   */
  async connect(userData) {
    // Уже подключены — ничего не делаем.
    if (this._socket?.connected && this._sessionId) {
      return { sessionId: this._sessionId };
    }

    // session/start вызываем только один раз за жизненный цикл страницы.
    if (!this._sessionId) {
      if (!this._startPromise) {
        this._startPromise = this._startSession(userData);
      }
      try {
        const { sessionId, status } = await this._startPromise;
        this._sessionId = sessionId;

        if (this._isClosedStatus(status)) {
          this._setNeedsResume(true);
          return { sessionId: this._sessionId, closed: true };
        }
      } catch (error) {
        this._startPromise = null;
        throw error;
      }
    }

    if (!this._sessionId) {
      throw new Error('Chat session_id was not initialized');
    }

    // Сессия уже помечена как закрытая (например, после session_status: close).
    if (this._needsResume) {
      return { sessionId: this._sessionId, closed: true };
    }

    if (!this._connectPromise) {
      this._connectPromise = this._connectSocket().finally(() => {
        this._connectPromise = null;
      });
    }

    await this._connectPromise;
    return { sessionId: this._sessionId };
  }

  /** Отправить сообщение */
  async send(text) {
    const trimmed = String(text || '').trim();
    if (!trimmed) return;

    const messageId = `user-${Date.now()}`;
    const userMsg = {
      id: messageId,
      text: trimmed,
      author: 'user',
      ts: new Date().toISOString(),
    };
    this._emitMessage(userMsg);

    if (!this._socket?.connected || !this._sessionId) {
      this._emitSendError(messageId);
      throw new Error('Live chat is not connected');
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this._removePendingSend(messageId);
        this._emitSendError(messageId);
        reject(new Error('Message send timeout'));
      }, SEND_TIMEOUT_MS);

      this._pendingSends.push({ messageId, resolve, reject, timeoutId });

      this._socket.emit('user_message', {
        session_id: this._sessionId,
        text: trimmed,
        client_message_id: messageId,
      });
    });
  }

  /**
   * Подписаться на входящие сообщения.
   * @param {Function} callback - (message: { text, author, ts }) => void
   * @returns {Function} unsubscribe
   */
  subscribe(callback) {
    this._listeners.push(callback);
    return () => {
      this._listeners = this._listeners.filter(l => l !== callback);
    };
  }

  /** Активно ли Socket.IO-соединение */
  isConnected() {
    return Boolean(this._socket?.connected);
  }

  /**
   * Подписаться на изменение состояния подключения.
   * @param {Function} callback - (isConnected: boolean) => void
   * @returns {Function} unsubscribe
   */
  subscribeConnection(callback) {
    this._connectionListeners.push(callback);
    callback(this.isConnected());
    return () => {
      this._connectionListeners = this._connectionListeners.filter(l => l !== callback);
    };
  }

  /** Нужно ли показать кнопку возобновления чата */
  needsResume() {
    return this._needsResume;
  }

  /**
   * Подписаться на изменение флага needsResume.
   * @param {Function} callback - (needsResume: boolean) => void
   * @returns {Function} unsubscribe
   */
  subscribeNeedsResume(callback) {
    this._needsResumeListeners.push(callback);
    callback(this._needsResume);
    return () => {
      this._needsResumeListeners = this._needsResumeListeners.filter(l => l !== callback);
    };
  }

  /**
   * Возобновить сессию после разрыва соединения.
   * POST /chat/resume → переподключение сокета → загрузка истории.
   * @returns {Promise<Array>}
   */
  async resume() {
    if (!this._sessionId) {
      throw new Error('Chat session_id is not available');
    }

    await apiService.post(CHAT_RESUME_ENDPOINT, { session_id: this._sessionId });

    this._connectPromise = null;

    try {
      await this._connectSocket();
    } catch (error) {
      this._setNeedsResume(true);
      throw error;
    }

    this._setNeedsResume(false);

    const history = await this.getHistory();
    this._emitMessage({ kind: 'history', messages: history });
    return history;
  }

  /** Завершить сессию */
  disconnect() {
    this._setNeedsResume(false);
    this._failAllPendingSends(false);
    if (this._socket) {
      if (this._sessionId) {
        this._socket.emit('leave', { session_id: this._sessionId });
      }
      this._socket.disconnect();
      this._socket = null;
    }
    this._notifyConnection();
    // Не сбрасываем session_id, чтобы не делать повторный /chat/start.
  }

  /** Текущий session_id чата */
  getSessionId() {
    return this._sessionId;
  }

  /** Загрузить историю сообщений по session_id */
  async getHistory() {
    if (!this._sessionId) return [];

    const endpoint = CHAT_HISTORY_ENDPOINT_TEMPLATE.replace('{session_id}', this._sessionId);

    try {
      const response = await apiService.get(endpoint);
      const rawMessages = Array.isArray(response?.messages) ? response.messages : [];
      return rawMessages.map((message, index) => this._normalizeHistoryMessage(message, index));
    } catch (error) {
      console.warn('[LiveChat] Failed to load chat history:', error);
      return [];
    }
  }

  _emitMessage(message) {
    this._listeners.forEach(cb => cb(message));
  }

  _setNeedsResume(value) {
    if (this._needsResume === value) return;
    this._needsResume = value;
    this._needsResumeListeners.forEach(cb => cb(this._needsResume));
  }

  _notifyConnection() {
    const connected = this.isConnected();
    this._connectionListeners.forEach(cb => cb(connected));
  }

  _handleSessionClose() {
    if (this.isConnected()) {
      this._failAllPendingSends();
      if (this._socket) {
        this._socket.disconnect();
        this._socket = null;
      }
      this._notifyConnection();
    }

    this._emitMessage({
      id: `session-closed-${Date.now()}`,
      textKey: 'liveChat.sessionClosed',
      author: 'system',
      ts: new Date().toISOString(),
      kind: 'system',
    });

    this._setNeedsResume(true);
  }

  _emitSendError(relatedMessageId) {
    this._emitMessage({
      id: `send-error-${Date.now()}`,
      errorKey: 'liveChat.sendError',
      author: 'manager',
      ts: new Date().toISOString(),
      kind: 'error',
      relatedMessageId,
    });
  }

  _removePendingSend(messageId) {
    const index = this._pendingSends.findIndex(item => item.messageId === messageId);
    if (index === -1) return null;

    const [pending] = this._pendingSends.splice(index, 1);
    clearTimeout(pending.timeoutId);
    return pending;
  }

  _resolvePendingSend(messageId) {
    const pending = this._removePendingSend(messageId);
    if (!pending) return false;
    pending.resolve();
    return true;
  }

  _failAllPendingSends(showError = true) {
    while (this._pendingSends.length > 0) {
      const pending = this._pendingSends.shift();
      clearTimeout(pending.timeoutId);
      if (showError) {
        this._emitSendError(pending.messageId);
      }
      pending.reject(new Error('Live chat connection lost'));
    }
  }

  _handleMessageSent(payload) {
    const clientMessageId = payload?.client_message_id || payload?.clientMessageId;

    if (clientMessageId && this._resolvePendingSend(clientMessageId)) {
      return;
    }

    const oldestPending = this._pendingSends[0];
    if (oldestPending) {
      this._resolvePendingSend(oldestPending.messageId);
    }
  }

  _normalizeManagerMessage(raw) {
    const text = raw?.text || raw?.message || '';
    return {
      id: raw?.id || `manager-${Date.now()}`,
      text: String(text),
      author: 'manager',
      ts: raw?.ts || raw?.timestamp || new Date().toISOString(),
    };
  }

  _normalizeSocketError(raw) {
    const code = raw?.code || 'WS_ERROR';
    const message = raw?.message || 'WebSocket error';
    return {
      id: `system-error-${Date.now()}`,
      text: `[${code}] ${message}`,
      author: 'manager',
      ts: new Date().toISOString(),
      kind: 'error',
    };
  }

  _isClosedStatus(status) {
    const normalized = String(status || '').toLowerCase();
    return normalized === 'close' || normalized === 'closed';
  }

  _normalizeHistoryMessage(raw, index) {
    const source = String(raw?.source || '').toLowerCase();
    const author = source === 'user' ? 'user' : 'manager';
    return {
      id: `history-${raw?.created_at || 'no-ts'}-${index}`,
      text: String(raw?.text || ''),
      author,
      ts: raw?.created_at || new Date().toISOString(),
    };
  }

  async _startSession(userData) {
    let leadData = userData?.leadData || {};

    const hasContacts =
      leadData.name || leadData.email || leadData.phone || leadData.company;

    // Если контакты ещё не успели попасть в state — подтягиваем с бэка.
    if (!hasContacts && userData?.id) {
      const profile = await fetchChatUserProfile(userData.id);
      const mapped = mapChatUserToLeadData(profile);
      if (mapped) {
        leadData = { ...leadData, ...mapped };
      }
    }

    const startPayload = {
      session_id: userData?.id || null,
      language: userData?.language || 'en',
    };

    const name = String(leadData.name || '').trim();
    const email = String(leadData.email || '').trim();
    const phone = String(leadData.phone || '').trim();
    const company = String(leadData.company || '').trim();

    if (name) startPayload.user_name = name;
    if (email) startPayload.user_email = email;
    if (phone) startPayload.user_phone = phone;
    if (company) startPayload.user_company = company;

    const startResponse = await apiService.post(CHAT_START_ENDPOINT, startPayload);
    const sessionId = startResponse?.session_id || startResponse?.sessionId;
    const status = startResponse?.status;

    if (!sessionId) {
      throw new Error('Chat session_id was not returned by backend');
    }

    return { sessionId, status };
  }

  _connectSocket() {
    return new Promise((resolve, reject) => {
      if (this._socket?.connected) {
        resolve();
        return;
      }

      if (this._socket) {
        this._socket.disconnect();
        this._socket = null;
      }

      const socket = io(SOCKET_URL, {
        path: SOCKET_PATH,
        transports: ['websocket'],
        withCredentials: true,
        reconnection: false,
      });

      const cleanup = () => {
        socket.off('connect', handleConnect);
        socket.off('connect_error', handleConnectError);
        socket.off('joined', handleJoined);
        socket.off('error', handleSocketError);
        clearTimeout(joinTimeout);
      };

      const handleConnect = () => {
        this._socket = socket;
        socket.emit('join', { session_id: this._sessionId });
      };

      const handleConnectError = (error) => {
        cleanup();
        socket.disconnect();
        reject(error || new Error('Socket connection failed'));
      };

      const handleJoined = (payload) => {
        const joinedSessionId = payload?.session_id || payload?.sessionId;
        if (joinedSessionId && joinedSessionId !== this._sessionId) return;
        cleanup();
        this._notifyConnection();
        resolve();
      };

      const handleSocketError = (payload) => {
        const normalized = this._normalizeSocketError(payload);
        this._emitMessage(normalized);
      };

      socket.on('connect', handleConnect);
      socket.on('connect_error', handleConnectError);
      socket.on('joined', handleJoined);
      socket.on('error', handleSocketError);
      socket.on('manager_message', (payload) => {
        this._emitMessage(this._normalizeManagerMessage(payload));
      });
      socket.on('message_sent', (payload) => {
        this._handleMessageSent(payload);
      });
      socket.on('session_status', (payload) => {
        const status = String(payload?.status || '').toLowerCase();

        if (status === 'close' || status === 'closed') {
          this._handleSessionClose();
          return;
        }

        this._emitMessage({
          id: `session-status-${Date.now()}`,
          text: `Session status: ${status || 'unknown'}`,
          author: 'manager',
          ts: new Date().toISOString(),
          kind: 'status',
        });
      });
      socket.on('disconnect', () => {
        this._failAllPendingSends();
        this._socket = null;
        this._notifyConnection();
        if (this._sessionId) {
          this._setNeedsResume(true);
        }
        console.warn('[LiveChat] Socket disconnected');
      });

      const joinTimeout = setTimeout(() => {
        cleanup();
        socket.disconnect();
        reject(new Error('Join timeout: no "joined" event received'));
      }, 10000);
    });
  }
}

export const liveChatService = new LiveChatService();

/** Преобразует ответ GET /chat/user/<session_id> в поля leadData */
export function mapChatUserToLeadData(data) {
  if (!data) return null;

  const mapped = {};
  if (data.user_name) mapped.name = data.user_name;
  if (data.user_company) mapped.company = data.user_company;
  if (data.user_email) mapped.email = data.user_email;
  if (data.user_phone) mapped.phone = data.user_phone;

  return Object.keys(mapped).length > 0 ? mapped : null;
}

/** Загрузить сохранённые контактные данные пользователя по session_id */
export async function fetchChatUserProfile(sessionId) {
  if (!sessionId) return null;

  const endpoint = CHAT_USER_ENDPOINT_TEMPLATE.replace('{session_id}', sessionId);

  try {
    return await apiService.get(endpoint);
  } catch (error) {
    console.warn('[LiveChat] Failed to load user profile:', error);
    return null;
  }
}
